import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// Cart add-ons, kept in sync after every cart change:
//  1. Engraving — each engraved product line (properties _engrave_sides / _engrave_variant) gets a
//     non-stocked "Engraving" line: qty = product qty × sides engraved.
//  2. Gift wrap — keeps the gift-wrap lines matching what the [data-apts-gw] panel says they
//     should be (worked out in Liquid from the cart).
const SOURCE = 'apts-giftwrap';
const ENGRAVING = 'engraving';
let busy = false;

const sectionIds = () =>
  [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);

const panel = () => document.querySelector('[data-apts-gw]');

const getCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

function sig(lines) {
  return lines.map((l) => `${l.id}:${l.qty}:${l.wf || ''}`).sort().join('|');
}

async function post(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...body, sections: sectionIds().join(','), sections_url: window.location.pathname })
  });
  return res.json();
}

function refresh(data) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.body.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise }));
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
    detail: { items: data.items, itemCount: data.item_count, sections: data.sections, source: SOURCE, didError: false }
  });
  setTimeout(reconcile, 450);
}

// Returns fresh cart data (with sections) if anything changed, otherwise null.
async function syncEngraving() {
  const cart = await getCart();
  const lines = cart.items.filter((i) => i.handle === ENGRAVING);
  const parents = cart.items.filter(
    (i) => i.handle !== ENGRAVING && Number(i.properties?._engrave_sides) > 0 && i.properties?._engrave_variant
  );
  if (!lines.length && !parents.length) return null;

  const updates = {};
  const adds = [];
  const used = new Set();
  for (const p of parents) {
    const d = {
      eid: p.properties._engrave_id || p.key,
      id: Number(p.properties._engrave_variant),
      qty: p.quantity * Number(p.properties._engrave_sides),
      parent: p.key,
      variant: p.variant_id,
      line: p,
      for: `${p.product_title} – ${p.properties.Engraving || 'engraving'}`
    };
    const a = lines.find((l) => !used.has(l.key) && l.variant_id === d.id && l.properties?._engrave_for === d.eid);
    const outOfOrder = a && !a.parent_relationship && cart.items.indexOf(a) < cart.items.indexOf(p);
    if (a && !outOfOrder) {
      used.add(a.key);
      if (a.quantity !== d.qty) updates[a.key] = d.qty;
    } else {
      // Missing, or sitting above its product at checkout: (re)add it so it nests / lands below.
      if (a) {
        used.add(a.key);
        updates[a.key] = 0;
      }
      adds.push(d);
    }
  }
  lines.forEach((l) => { if (!used.has(l.key)) updates[l.key] = 0; });
  if (!Object.keys(updates).length && !adds.length) return null;

  let data;
  if (Object.keys(updates).length) data = await post('/cart/update.js', { updates });
  for (const d of adds) {
    // The engraving line carries the details (text, font, logo file, approval) for the engraver;
    // the product line only says "Engraving: Added (Lid)".
    const pp = d.line.properties || {};
    // Kept short so it reads tightly at checkout: one line per side, e.g.
    //   Lid: "Love, Mom" · Classic font     /     Base logo: <file link>
    const props = { For: d.line.product_title };
    const sides = new Set(Object.keys(pp).filter((k) => / (text|logo|font)$/.test(k)).map((k) => k.slice(1).replace(/ (text|logo|font)$/, '')));
    sides.forEach((s) => {
      const text = pp[`_${s} text`];
      const font = (pp[`_${s} font`] || '').replace(/\s*\(.*\)$/, '');
      const logo = pp[`_${s} logo`];
      const own = pp[`_${s} font name`];
      if (text) props[s] = `"${text}" · ${own ? `${own} font (customer's choice, to confirm)` : `${font || 'Classic'} font`}`;
      if (logo) props[`${s} logo`] = logo;
    });
    if (Object.keys(props).some((k) => k.endsWith(' logo'))) props.Layout = 'Designer to send for approval';
    if (pp['_Engraving notes']) props.Notes = pp['_Engraving notes'];
    if (pp['_Engraving approved']) props.Approved = 'Yes';
    props._engrave_for = d.eid;
    const item = { id: d.id, quantity: d.qty, properties: props };
    // Try to nest the engraving under its product (parent by line key, then by variant id).
    let res = await post('/cart/add.js', { items: [{ ...item, parent_id: d.parent }] });
    if (res.status) {
      console.warn('[engraving] nest by key failed', res);
      res = await post('/cart/add.js', { items: [{ ...item, parent_id: d.variant }] });
    }
    if (!res.status) {
      data = res;
      continue;
    }
    console.warn('[engraving] nest by variant failed', res);
    // Not nested: add it on its own, then re-add the product so it sits directly above its
    // engraving in the cart and at checkout (both list the newest line first).
    res = await post('/cart/add.js', { items: [item] });
    if (res.status) continue;
    data = res;
    const p = d.line;
    await post('/cart/update.js', { updates: { [p.key]: 0 } });
    const again = await post('/cart/add.js', { items: [{ id: p.variant_id, quantity: p.quantity, properties: p.properties }] });
    if (!again.status) data = again;
  }
  const fresh = await getCart();
  return { ...fresh, sections: data?.sections };
}

async function reconcile() {
  if (busy) return;
  busy = true;
  try {
    const engraved = await syncEngraving();
    if (engraved) {
      busy = false;
      refresh(engraved);
      return;
    }
  } catch (e) {
    console.warn('[engraving]', e);
  }
  busy = false;
  await reconcileWrap();
  await reconcileTags();
}

const anchorKey = () => null;
const addNested = (items) => post('/cart/add.js', { items });

async function reconcileWrap() {
  const el = panel();
  if (busy) return;
  let desired, actual;
  if (el) {
    try { desired = JSON.parse(el.dataset.desired || '[]'); actual = JSON.parse(el.dataset.actual || '[]'); } catch (e) { return; }
    // One gift-wrap line per wrap size (e.g. "Medium × 3 — For: Compass × 2, Placemats"), not one per product.
    const groups = new Map();
    desired.forEach((d) => {
      const g = groups.get(d.id) || { id: d.id, qty: 0, names: new Map(), wfs: [] };
      g.qty += d.qty;
      // Same product on two cart lines (e.g. one engraved, one not) is counted once: "Magnetic Compass × 2"
      g.names.set(d.for, (g.names.get(d.for) || 0) + (d.wf ? d.qty : 1));
      if (d.wf) g.wfs.push(d.wf);
      groups.set(d.id, g);
    });
    desired = [...groups.values()].map((g) => ({
      id: g.id,
      qty: g.qty,
      for: [...g.names].map(([n, q]) => (q > 1 ? `${n} × ${q}` : n)).join(', '),
      wf: g.wfs.join(',')
    }));
  } else {
    // No eligible items left (or panel not rendered): remove any stray wrap lines
    const cart = await getCart();
    actual = cart.items.filter((i) => i.handle === 'gift-wrapping').map((i) => ({ key: i.key, id: i.variant_id, qty: i.quantity }));
    desired = [];
    const hasProducts = cart.items.some((i) => !['gift-wrapping', ENGRAVING].includes(i.handle));
    if (!hasProducts && (cart.attributes?._gift_wrap_mode || 'none') !== 'none') {
      // Cart emptied: switch gift wrap off so it isn't added automatically to the next product.
      busy = true;
      try {
        const cards = {};
        Object.keys(cart.attributes || {}).forEach((k) => { if (k.startsWith('Card for ')) cards[k] = ''; });
        await post('/cart/update.js', { attributes: { _gift_wrap_mode: 'none', _gift_wrap_skip: '', 'Gift wrap': '', 'Gift message': '', ...cards } });
      } finally {
        busy = false;
      }
    }
    if (!actual.length) return;
  }
  if (sig(desired) === sig(actual)) return;
  busy = true;
  try {
    let data;
    if (actual.length) {
      const updates = {};
      actual.forEach((l) => (updates[l.key] = 0));
      data = await post('/cart/update.js', { updates });
    }
    if (desired.length) {
      const now = await getCart();
      for (const d of desired) {
        const ids = d.wf ? d.wf.split(',') : null;
        data = await addNested(
          [{ id: d.id, quantity: d.qty, properties: { For: d.for, ...(d.wf ? { _wrap_for: d.wf } : {}) } }],
          anchorKey(now.items, ids)
        );
      }
      const cart = await getCart();
      data = { ...cart, sections: data.sections };
    }
    if (data) refresh(data);
  } catch (e) {
    console.warn('[gift wrap]', e);
  } finally {
    busy = false;
  }
}

const idle = async () => { while (busy) await new Promise((r) => setTimeout(r, 120)); };

async function setAttrs(attrs) {
  await idle();
  busy = true;
  try {
    if (typeof flushMsgs === 'function') await flushMsgs();
    const data = await post('/cart/update.js', { attributes: attrs });
    busy = false;
    refresh(data);
  } catch (e) {
    busy = false;
    console.warn('[gift wrap]', e);
  }
}

// Card messages → cart attributes, shown on the order in admin:
//   "Gift message"            one card (single item, or everything wrapped together)
//   "Card for <product>"      one card per gift when wrapped separately
// Saved quietly while typing (max 255 characters) so the cart doesn't re-render under the shopper.
const MSG_MAX = 255;
// All pending card edits go in ONE request at a time (parallel cart updates overwrite each other).
const lastMsg = {};
let pendingMsgs = {};
let msgTimer;
let msgSaving = Promise.resolve();
function flushMsgs() {
  clearTimeout(msgTimer);
  const attrs = pendingMsgs;
  pendingMsgs = {};
  if (!Object.keys(attrs).length) return msgSaving;
  msgSaving = msgSaving.then(() => fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: attrs }),
    keepalive: true
  })).catch((err) => console.warn('[gift message]', err));
  return msgSaving;
}
function saveMsg(key, value, now = true) {
  const v = value.slice(0, MSG_MAX).trim();
  if (v === lastMsg[key]) return;
  lastMsg[key] = v;
  pendingMsgs[key] = v;
  clearTimeout(msgTimer);
  if (now) flushMsgs(); else msgTimer = setTimeout(flushMsgs, 600);
}
const msgTimers = {};
// Blank every per-item card ("Card for …") so old messages don't end up on the order.
async function cardsCleared() {
  const cart = await getCart();
  const out = {};
  Object.keys(cart.attributes || {}).forEach((k) => { if (k.startsWith('Card for ') || k.startsWith('_card_')) out[k] = ''; });
  return out;
}
document.addEventListener('input', (e) => {
  const t = e.target;
  if (!t.matches?.('[data-gw-msg]')) return;
  const key = t.dataset.gwMsgKey || 'Gift message';
  if (t.value.length > MSG_MAX) t.value = t.value.slice(0, MSG_MAX);
  // Same card shown in the drawer and on the cart page: keep both copies in step.
  document.querySelectorAll('[data-gw-msg]').forEach((ta) => {
    if (ta !== t && (ta.dataset.gwMsgKey || 'Gift message') === key) ta.value = t.value;
  });
  document.querySelectorAll('[data-gw-msg]').forEach((ta) => {
    const c = ta.closest('label')?.querySelector('[data-gw-count]');
    if (c) c.textContent = ta.value.length;
  });
  saveMsg(key, t.value, false);
});

document.addEventListener('change', async (e) => {
  const t = e.target;
  const el = t.closest?.('[data-apts-gw]');
  if (!el) return;
  if (t.matches('[data-gw-msg]')) {
    saveMsg(t.dataset.gwMsgKey || 'Gift message', t.value);
  } else if (t.matches('[data-gw-on]')) {
    setAttrs(t.checked
      ? { _gift_wrap_mode: 'together', 'Gift wrap': 'All items wrapped together' }
      : { _gift_wrap_mode: 'none', _gift_wrap_skip: '', 'Gift wrap': '', 'Gift message': '', Card: '', ...(await cardsCleared()) });
  } else if (t.matches('[data-gw-mode]')) {
    const extra = t.value === 'together' ? await cardsCleared() : {};
    setAttrs({ _gift_wrap_mode: t.value, 'Gift wrap': t.value === 'together' ? 'All items wrapped together' : 'Each item wrapped separately', ...extra });
  } else if (t.matches('[data-gw-count]')) {
    const counts = [...el.querySelectorAll('[data-gw-count]')].map((s) => `${s.dataset.gwCount}=${s.value}`).join(',');
    setAttrs({ _gift_wrap_qty: counts });
  } else if (t.matches('[data-gw-item]')) {
    const skip = [...el.querySelectorAll('[data-gw-item]')].filter((c) => !c.checked).map((c) => c.dataset.gwItem).join(',');
    const extra = {};
    if (!t.checked) {
      const key = t.closest('.apts-gw__itemwrap')?.querySelector('[data-gw-msg]')?.dataset.gwMsgKey;
      if (key) extra[key] = '';
    }
    setAttrs({ _gift_wrap_skip: skip, ...extra });
  }
});

// Card choice: free standard card, or a gift tag (product "gift-tag") picked in a popup.
// Wrapped together / single gift: one tag (_tag_for "all"). Wrapped separately: one tag per wrapped
// product line (_tag_for = variant id, qty = that product's qty). The panel's data-tag-want says which
// tags are allowed and how many; reconcileTags() trims anything that no longer applies.
const CARD_STANDARD = 'Standard A PLACE TO SHOP card';
const isTag = (i) => ['gift-tag', 'apts-card'].includes(i.handle) && i.properties?._gift_tag;
const tagFor = (i) => i.properties?._tag_for || 'all';
const msgFor = (key) => document.querySelector(`[data-gw-msg][data-card-for="${CSS.escape(key)}"]`)?.value.trim() || '';

function cardSummary(items) {
  return items.filter(isTag).map((t) => {
    const name = t.handle === 'apts-card' ? 'Standard card' : `Gift tag ${t.variant_title}`;
    return `${name}${tagFor(t) === 'all' ? '' : ` for ${t.properties.For}`}${t.quantity > 1 ? ` × ${t.quantity}` : ''}`;
  }).join('; ');
}

async function setTag(key, variant, qty, name, extra = {}) {
  await idle();
  busy = true;
  try {
    let cart = await getCart();
    const updates = {};
    cart.items.filter((i) => isTag(i) && tagFor(i) === key).forEach((i) => { updates[i.key] = 0; });
    if (Object.keys(updates).length) await post('/cart/update.js', { updates });
    if (variant) {
      const message = msgFor(key);
      await addNested([{
        id: Number(variant),
        quantity: qty,
        properties: { _gift_tag: '1', _tag_for: key, For: key === 'all' ? 'Gift wrap' : name, ...(message ? { Message: message } : {}) }
      }]);
    }
    cart = await getCart();
    if (typeof flushMsgs === 'function') await flushMsgs();
    const attrs = { [`_card_on_${key}`]: '1', ...extra };
    const data = await post('/cart/update.js', { attributes: { ...attrs, Card: cardSummary(cart.items) } });
    busy = false;
    refresh(data);
  } catch (e) {
    busy = false;
    console.warn('[gift tags]', e);
  }
}

async function reconcileTags() {
  if (busy) return;
  let want = {};
  const el = panel();
  if (el) {
    try { want = JSON.parse(el.dataset.tagWant || '{}'); } catch (e) { return; }
  }
  const cart = await getCart();
  const tags = cart.items.filter(isTag);
  if (!tags.length) return;
  const updates = {};
  tags.forEach((i) => {
    const k = tagFor(i);
    if (!(k in want)) updates[i.key] = 0;
    else if (want[k] !== i.quantity) updates[i.key] = want[k];
  });
  if (!Object.keys(updates).length) return;
  busy = true;
  try {
    const left = tags.filter((i) => updates[i.key] !== 0);
    const data = await post('/cart/update.js', { updates, attributes: { Card: cardSummary(left) } });
    busy = false;
    refresh(data);
  } catch (e) {
    busy = false;
    console.warn('[gift tags]', e);
  }
}

function openTags(from) {
  const dlg = from.closest('[data-apts-gw]')?.querySelector('[data-gw-tags]');
  if (!dlg) return;
  dlg.dataset.for = from.dataset.for || 'all';
  dlg.dataset.qty = from.dataset.qty || '1';
  dlg.dataset.forName = from.dataset.forName || '';
  dlg.dataset.picked = '';
  dlg.querySelector('[data-tags-for]').textContent = dlg.dataset.for === 'all' ? '' : `For ${dlg.dataset.forName}${Number(dlg.dataset.qty) > 1 ? ` (× ${dlg.dataset.qty})` : ''}. `;
  dlg.showModal();
}

// "Edit message" on a card / gift tag line in the cart: jump to that card's message box.
document.addEventListener('click', (e) => {
  const btn = e.target.closest?.('[data-gw-edit-msg]');
  if (!btn) return;
  e.preventDefault();
  const scope = btn.closest('cart-drawer-component, dialog, .cart-items__wrapper')?.parentElement || document;
  const box = scope.querySelector(`[data-gw-msg][data-card-for="${CSS.escape(btn.dataset.gwEditMsg)}"]`)
    || document.querySelector(`[data-gw-msg][data-card-for="${CSS.escape(btn.dataset.gwEditMsg)}"]`);
  if (!box) return;
  box.scrollIntoView({ block: 'center', behavior: 'smooth' });
  box.focus({ preventScroll: true });
  box.setSelectionRange(box.value.length, box.value.length);
  const wrap = box.closest('.apts-gw__msg');
  wrap?.classList.add('is-flash');
  setTimeout(() => wrap?.classList.remove('is-flash'), 1600);
});

document.addEventListener('click', (e) => {
  const t = e.target;
  const open = t.closest?.('[data-gw-tags-open]');
  if (open) {
    e.preventDefault();
    openTags(open);
    return;
  }
  const dlg = t.closest?.('[data-gw-tags]');
  if (!dlg) return;
  const pick = t.closest('[data-tag-pick]');
  if (pick && !pick.disabled) {
    dlg.dataset.picked = '1';
    dlg.close();
    setTag(dlg.dataset.for, pick.dataset.tagPick, Number(dlg.dataset.qty) || 1, dlg.dataset.forName);
  } else if (t.closest('[data-gw-tags-close]') || t === dlg) {
    dlg.close();
  }
});

document.addEventListener('close', (e) => {
  const dlg = e.target;
  if (!dlg.matches?.('[data-gw-tags]') || dlg.dataset.picked) return;
  const gw = dlg.closest('[data-apts-gw]');
  const radio = gw?.querySelector(`[data-gw-card][value="tag"][data-for="${dlg.dataset.for}"]`);
  if (radio?.checked && !radio.closest('label').querySelector('.apts-gw__tagthumb')) {
    gw.querySelector(`[data-gw-card][value="standard"][data-for="${dlg.dataset.for}"]`).checked = true;
  }
}, true);

document.addEventListener('change', (e) => {
  const t = e.target;
  if (t.matches?.('[data-gw-cardon]')) {
    const key = t.dataset.for || 'all';
    if (t.checked) setAttrs({ [`_card_on_${key}`]: '1' });
    else {
      delete lastMsg[t.dataset.msgKey];
      delete pendingMsgs[t.dataset.msgKey];
      setTag(key, null, 1, '', { [`_card_on_${key}`]: '', [t.dataset.msgKey]: '' });
    }
    return;
  }
  if (!t.matches?.('[data-gw-card]')) return;
  if (t.value === 'tag') openTags(t);
  else {
    const gw = t.closest('[data-apts-gw]');
    const tagRadio = t.closest('.apts-cardpick__opts')?.querySelector('[data-gw-card][value="tag"]');
    setTag(t.dataset.for || 'all', gw?.dataset.cardVariant, Number(tagRadio?.dataset.qty) || 1, tagRadio?.dataset.forName || '');
  }
});

// Keep the card message on the card / tag line too, so it shows at checkout and on the order.
document.addEventListener('change', async (e) => {
  const t = e.target;
  if (!t.matches?.('[data-gw-msg][data-card-for]')) return;
  const key = t.dataset.cardFor;
  const message = t.value.slice(0, MSG_MAX).trim();
  await idle();
  busy = true;
  try {
    await flushMsgs();
    const cart = await getCart();
    const line = cart.items.find((i) => isTag(i) && tagFor(i) === key);
    if (!line || (line.properties.Message || '') === message) { busy = false; return; }
    const properties = { ...line.properties };
    if (message) properties.Message = message; else delete properties.Message;
    const data = await post('/cart/change.js', { id: line.key, quantity: line.quantity, properties });
    busy = false;
    refresh(data);
  } catch (err) {
    busy = false;
    console.warn('[card message]', err);
  }
});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise?.then(({ detail } = {}) => {
    if (detail?.source === SOURCE || detail?.didError) return;
    setTimeout(reconcile, 450);
  }).catch(() => {});
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reconcile); else reconcile();

// Checkout button submits the cart form with positional updates[] quantities. Cart rows are reordered
// on screen (add-ons under their product), so those positions no longer match the cart lines and
// Shopify would apply the wrong quantities. Quantities are already saved by AJAX: don't send them.
document.addEventListener('submit', async (e) => {
  const form = e.target;
  const qty = form.querySelectorAll?.('[name="updates[]"]');
  if (!qty?.length) return;
  qty.forEach((i) => { i.disabled = true; });
  if (!busy && !Object.keys(pendingMsgs).length) return;
  e.preventDefault();
  await flushMsgs();
  await idle();
  window.location.href = '/checkout';
}, true);


// Discount code box: show messages inside the field instead of a separate error line / browser bubble.
(() => {
  const showInField = (input, msg) => {
    input.value = '';
    input.placeholder = msg;
    input.classList.add('apts-input-error');
  };
  const reset = (input) => {
    if (!input.classList.contains('apts-input-error')) return;
    input.classList.remove('apts-input-error');
    input.placeholder = input.dataset.aptsPlaceholder || 'Discount code';
  };
  document.addEventListener('invalid', (e) => {
    const input = e.target;
    if (!input.matches?.('.cart-discount__input')) return;
    e.preventDefault();
    input.dataset.aptsPlaceholder ||= input.placeholder;
    showInField(input, 'Please enter a discount code');
  }, true);
  document.addEventListener('input', (e) => { if (e.target.matches?.('.cart-discount__input')) reset(e.target); });
  new MutationObserver(() => {
    document.querySelectorAll('.cart-discount__error:not(.hidden)').forEach((box) => {
      const msg = [...box.querySelectorAll('.cart-discount__error-text:not(.hidden)')].map((s) => s.textContent.trim()).join(' ');
      const input = box.closest('cart-discount-component')?.querySelector('.cart-discount__input');
      if (!input || !msg) return;
      input.dataset.aptsPlaceholder ||= input.placeholder;
      showInField(input, msg.replace(/^Discount code /i, 'Code ').replace(/\s*cannot be applied to your cart\.?/i, " can't be used on this cart"));
      box.classList.add('hidden');
    });
  }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class'], childList: true });
})();
